import React from 'react';
import { AlertTriangle, MessageCircle, RefreshCw } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { Button } from '../../components/ui/Button';

export const PreviewErrorExperience = ({ errorMessage, onRetry, isRetrying = false }) => {
  const { t } = useI18n();

  return (
    <div className="relative flex min-h-[560px] flex-1 items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_20%_14%,rgba(244,63,94,0.14)_0%,transparent_42%),radial-gradient(circle_at_80%_86%,rgba(249,115,22,0.12)_0%,transparent_40%),radial-gradient(circle_at_top,#fff7f7_0%,#fff1f2_45%,#f8fafc_100%)] p-6 dark:bg-[radial-gradient(circle_at_20%_14%,rgba(244,63,94,0.16)_0%,transparent_42%),radial-gradient(circle_at_80%_86%,rgba(251,146,60,0.1)_0%,transparent_40%),radial-gradient(circle_at_top,#2a1218_0%,#1e1b2e_50%,#020617_100%)]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="preview-error-orb preview-error-orb-a" />
        <div className="preview-error-orb preview-error-orb-b" />
        <div className="preview-error-grid" aria-hidden="true" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        <div className="relative mx-auto flex h-28 w-28 items-center justify-center">
          <span className="preview-error-ring preview-error-ring-outer" aria-hidden="true" />
          <span className="preview-error-ring preview-error-ring-inner" aria-hidden="true" />
          <div className="relative flex h-16 w-16 items-center justify-center rounded-[1.35rem] bg-[linear-gradient(135deg,#be123c_0%,#e11d48_55%,#f97316_100%)] text-white shadow-[0_20px_50px_rgba(190,18,60,0.32)] dark:shadow-[0_20px_50px_rgba(244,63,94,0.24)]">
            <AlertTriangle size={30} strokeWidth={2.2} />
          </div>
        </div>

        <h3 className="mt-4 text-center text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
          {t('previewErrorTitle')}
        </h3>
        <p className="mt-2 text-center text-sm leading-7 text-slate-600 dark:text-slate-300">{t('previewErrorSubtitle')}</p>

        <div className="mt-5 overflow-hidden rounded-[1.2rem] border border-rose-200/70 bg-white/85 shadow-sm dark:border-rose-500/20 dark:bg-slate-950/55">
          <div className="flex items-center justify-between gap-3 border-b border-rose-100/80 bg-rose-50/70 px-4 py-2.5 dark:border-rose-500/15 dark:bg-rose-500/10">
            <span className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-rose-700 dark:text-rose-200">
              {t('previewErrorDetailsLabel')}
            </span>
            <Button
              type="button"
              variant="danger"
              onClick={onRetry}
              disabled={isRetrying}
              className="shrink-0 rounded-lg px-0 py-0 text-[0.6875rem] font-light shadow-[0_6px_14px_rgba(190,18,60,0.18)]"
            >
              {isRetrying ? (
                <RefreshCw size={12} className="animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw size={12} aria-hidden="true" />
              )}
              {t('retryPreview')}
            </Button>
          </div>
          {errorMessage ? (
            <p className="max-h-28 overflow-auto px-4 py-3 text-start font-mono text-xs leading-6 text-rose-900/90 dark:text-rose-100/90" dir="ltr">
              {errorMessage}
            </p>
          ) : null}
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <div className="rounded-[1.25rem] border border-white/70 bg-white/82 p-4 text-start shadow-[0_16px_40px_rgba(15,23,42,0.08)] backdrop-blur dark:border-white/10 dark:bg-slate-950/50">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300">
              <RefreshCw size={17} strokeWidth={2.2} />
            </span>
            <p className="mt-3 text-sm leading-7 text-slate-700 dark:text-slate-200">{t('previewErrorHintRetry')}</p>
          </div>
          <div className="rounded-[1.25rem] border border-white/70 bg-white/82 p-4 text-start shadow-[0_16px_40px_rgba(15,23,42,0.08)] backdrop-blur dark:border-white/10 dark:bg-slate-950/50">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[rgba(18,54,41,0.1)] text-[#123629] dark:bg-teal-400/10 dark:text-teal-200">
              <MessageCircle size={17} strokeWidth={2.2} />
            </span>
            <p className="mt-3 text-sm leading-7 text-slate-700 dark:text-slate-200">{t('previewErrorHintChat')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
